import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  Container,
  Button,
  Chip,
  Avatar,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Fade
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Inbox as InboxIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Mail as MailIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../consts';
import { useSelector } from 'react-redux';
import ConfirmationDialog from '../common/ConfirmationDialog';

const PERSONAL_SUB_CONFIRM_MESSAGE = 'You currently have an active personal subscription. By joining the organization\'s license, your personal subscription will be cancelled, and your account will be moved under the organization\'s subscription.';

// Component for displaying user's pending invitations (exported for use in /invitations)
export const OrganizationInvitationCard = ({ invitation, onUpdate }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState({ accept: false, reject: false, checkSubscription: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  const handleResponse = async (accepted) => {
    setLoading(prev => ({ ...prev, [accepted ? 'accept' : 'reject']: true }));
    
    try {
      const response = await axios.post(`${API_URL}/tenants/invitations/${invitation.id}`, {
        accepted
      });
      
      const msg = accepted
        ? (response.data?.personal_subscription_cancelled
          ? 'Successfully joined the organization! Your personal subscription has been cancelled.'
          : 'Successfully joined the organization!')
        : 'Invitation declined';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'success'
      });
      
      if (onUpdate) {
        onUpdate(invitation.id);
      }

      // If accepted, refresh user data
      if (accepted) {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Error responding to invitation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to process invitation. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, [accepted ? 'accept' : 'reject']: false }));
    }
  };

  const handleAcceptClick = async () => {
    setLoading(prev => ({ ...prev, checkSubscription: true }));
    try {
      const { data } = await axios.get(`${API_URL}/tenants/invitations/${invitation.id}/personal-subscription-status`);
      if (data?.has_active_personal_subscription) {
        setConfirmDialog({
          open: true,
          onConfirm: () => {
            setConfirmDialog(prev => ({ ...prev, open: false }));
            handleResponse(true);
          }
        });
      } else {
        await handleResponse(true);
      }
    } catch (error) {
      console.error('Error checking personal subscription:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to check subscription status. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, checkSubscription: false }));
    }
  };

  const organization = invitation.organization || {};
  const inviter = invitation.inviter || {};

  return (
    <>
      <Fade in timeout={500}>
        <Card 
          sx={{ 
            mb: 3,
            position: 'relative',
            overflow: 'visible',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              borderRadius: '4px 4px 0 0',
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Avatar
                sx={{ 
                  width: 56, 
                  height: 56, 
                  mr: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                <BusinessIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    {organization.name || 'Organization'}
                  </Typography>
                  <Chip
                    icon={<AccessTimeIcon fontSize="small" />}
                    label="Organization Invitation"
                    size="small"
                    color="primary"
                    sx={{ 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiChip-icon': { color: theme.palette.primary.main }
                    }}
                  />
                </Box>
                {inviter.first_name && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Invited by {inviter.first_name} {inviter.last_name}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {organization.category && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={organization.category}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main
                  }}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={loading.reject ? <CircularProgress size={20} color="error" /> : <CancelIcon />}
                onClick={() => handleResponse(false)}
                disabled={loading.accept || loading.reject || loading.checkSubscription}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                }}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                color="primary"
                endIcon={(loading.accept || loading.checkSubscription) ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleAcceptClick}
                disabled={loading.accept || loading.reject || loading.checkSubscription}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }
                }}
              >
                {loading.checkSubscription ? 'Checking...' : 'Accept'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={() => confirmDialog.onConfirm?.()}
        title="Personal subscription will be cancelled"
        message={PERSONAL_SUB_CONFIRM_MESSAGE}
        cancelButtonString="Cancel"
        confirmButtonString="Confirm and join organization"
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const OrganizationInvitations = () => {
  const theme = useTheme();
  const token = useSelector(state => state.token);
  const authorized = useSelector(state => state.authorized);
  const userData = useSelector((state) => state.user);
  const [tabValue, setTabValue] = useState(0);
  
  // User invitations state
  const [userInvitations, setUserInvitations] = useState([]);
  const [userInvitationsLoading, setUserInvitationsLoading] = useState(true);
  
  // Management invitations state
  const [managementInvitations, setManagementInvitations] = useState([]);
  const [managementLoading, setManagementLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deletingId, setDeletingId] = useState(null);

  const organizationId = userData?.organization_id;

  // Fetch user invitations
  const fetchUserInvitations = async () => {
    try {
      setUserInvitationsLoading(true);
      const response = await axios.get(`${API_URL}/tenants/invitations`);
      setUserInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching user invitations:', error);
    } finally {
      setUserInvitationsLoading(false);
    }
  };

  // Fetch management invitations
  const fetchManagementInvitations = async () => {
    if (!organizationId) return;
    
    try {
      setManagementLoading(true);
      const response = await axios.get(`${API_URL}/tenants/organizations/${organizationId}/invitations`);
      setManagementInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching management invitations:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load invitations',
        severity: 'error'
      });
    } finally {
      setManagementLoading(false);
    }
  };

  useEffect(() => {
    if (token && authorized) {
      fetchUserInvitations();
      if (organizationId) {
        fetchManagementInvitations();
      }
    }
  }, [token, authorized, organizationId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSendInvitation = async () => {
    if (!email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter an email address',
        severity: 'error'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    try {
      setSending(true);
      await axios.post(`${API_URL}/tenants/organizations/${organizationId}/invitations`, {
        email: email.trim().toLowerCase()
      });
      
      setSnackbar({
        open: true,
        message: 'Invitation sent successfully!',
        severity: 'success'
      });
      
      setEmail('');
      setOpenDialog(false);
      fetchManagementInvitations();
    } catch (error) {
      console.error('Error sending invitation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to send invitation',
        severity: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      setDeletingId(invitationId);
      await axios.delete(`${API_URL}/tenants/invitations/${invitationId}`);
      
      setSnackbar({
        open: true,
        message: 'Invitation revoked successfully',
        severity: 'success'
      });
      
      fetchManagementInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to revoke invitation',
        severity: 'error'
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleUserInvitationUpdate = (invitationId) => {
    setUserInvitations(prev => prev.filter(inv => inv.id !== invitationId));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AccessTimeIcon fontSize="small" />;
      case 'accepted':
        return <CheckCircleIcon fontSize="small" />;
      case 'rejected':
        return <CancelIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (!authorized) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Fade in timeout={500}>
          <Box 
            sx={{ 
              textAlign: 'center',
              py: 8,
              px: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <InboxIcon 
              sx={{ 
                fontSize: 64, 
                color: theme.palette.primary.main,
                mb: 2,
                opacity: 0.8
              }} 
            />
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Login Required
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                maxWidth: 500,
                mx: 'auto',
                mb: 3
              }}
            >
              Please log in to view your organization invitations.
            </Typography>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Organization Invitations
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="organization invitations tabs">
          <Tab 
            icon={<MailIcon />} 
            iconPosition="start"
            label="My Invitations" 
            sx={{ textTransform: 'none', minHeight: 64 }}
          />
          {organizationId && (
            <Tab 
              icon={<SettingsIcon />} 
              iconPosition="start"
              label="Manage Invitations" 
              sx={{ textTransform: 'none', minHeight: 64 }}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab Panel 0: My Invitations */}
      {tabValue === 0 && (
        <Box>
          {userInvitationsLoading ? (
            <Grid container spacing={3}>
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="40%" height={24} />
                        </Box>
                      </Box>
                      <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : userInvitations.length > 0 ? (
            <Grid container spacing={3}>
              {userInvitations.map((invitation, index) => (
                <Grid item xs={12} key={invitation.id || index}>
                  <OrganizationInvitationCard 
                    invitation={invitation} 
                    onUpdate={handleUserInvitationUpdate}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Fade in timeout={500}>
              <Box 
                sx={{ 
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 3,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <InboxIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: theme.palette.primary.main,
                    mb: 2,
                    opacity: 0.8
                  }} 
                />
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  No Invitations Yet
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    maxWidth: 500,
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  You haven't received any organization invitations yet. When someone invites you to join their organization, it will appear here.
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>
      )}

      {/* Tab Panel 1: Manage Invitations */}
      {tabValue === 1 && organizationId && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                }
              }}
            >
              Send Invitation
            </Button>
          </Box>

          {managementLoading ? (
            <Grid container spacing={3}>
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} key={index}>
                  <Card>
                    <CardContent>
                      <Skeleton variant="text" width="60%" height={32} />
                      <Skeleton variant="text" width="40%" height={24} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : managementInvitations.length === 0 ? (
            <Fade in timeout={500}>
              <Box 
                sx={{ 
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 3,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <BusinessIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: theme.palette.primary.main,
                    mb: 2,
                    opacity: 0.8
                  }} 
                />
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  No Invitations Yet
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    maxWidth: 500,
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  Start building your organization by inviting members via email.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenDialog(true)}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                  }}
                >
                  Send Your First Invitation
                </Button>
              </Box>
            </Fade>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {managementInvitations.map((invitation) => (
                    <TableRow key={invitation.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{invitation.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(invitation.status)}
                          label={invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                          color={getStatusColor(invitation.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {invitation.created_time ? new Date(invitation.created_time).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {invitation.updated_at ? new Date(invitation.updated_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {invitation.status === 'pending' && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteInvitation(invitation.id)}
                            disabled={deletingId === invitation.id}
                          >
                            {deletingId === invitation.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Send Invitation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon color="primary" />
            <Typography variant="h6">Send Invitation</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the email address of the person you want to invite to join your organization.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            disabled={sending}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendInvitation}
            variant="contained"
            disabled={sending || !email.trim()}
            startIcon={sending ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {sending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationInvitations;
